import styled from '@emotion/styled';
import React, { ReactNode } from 'react';
import { Button, ButtonGroup, pxToRem } from '@modernhealth/vital-components';

import { color, font, mediaQueries } from 'src/shared/styles/variables';

const TileWrapper = styled.div({
    padding: 20,
    marginLeft: 15,
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '32.5%',
    border: `1px solid ${color.linkWaterLight}`,
    alignItems: 'center',
    borderRadius: 12,
    [mediaQueries.sm]: {
        marginTop: 15,
        width: '90%',
    },
});

const TileTitle = styled.h4(
    {
        textAlign: 'center',
        color: color.fiord,
    },
    font.heavy(font.size.medium),
);

interface TileDescriptionProps {
    quote?: boolean;
}

const TileDescription = styled.p<TileDescriptionProps>(
    props => ({
        textAlign: 'center',
        color: props.quote ? color.blueBayoux : color.steelGray,
        fontStyle: props.quote ? 'oblique' : 'normal',
    }),
    font.book(font.size.base),
);

const IconContainer = styled.div({
    padding: 15,
    backgroundColor: color.lightCyan,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
});

interface TileProps {
    description: string;
    title?: string;
    img: string;
}

interface StyledIconProps {
    iconUrl: string;
}

const StyledIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    display: 'inline-block',
    backgroundImage: `url('${iconUrl}')`,
    width: '1.5rem',
    height: '1.5rem',
    backgroundSize: 'cover',
    backgroundColor: color.lightCyan,
    backgroundPositionX: 'center',
}));

const Tile: React.FC<TileProps> = ({ description, title, img }) => (
    <TileWrapper>
        <IconContainer>
            <StyledIcon iconUrl={img} />
        </IconContainer>
        {title && <TileTitle>{title}</TileTitle>}
        <TileDescription>{description}</TileDescription>
    </TileWrapper>
);

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    maxHeight: 1000,
    alignItems: 'center',
    justifyContent: 'center',
    [mediaQueries.sm]: {
        marginTop: 50,
    },
});

const Title = styled.h3(
    {
        color: color.pickledBluewood,
        marginTop: 32,
        marginBottom: 20,
    },
    font.black(font.size.headerMedium),
);

const Description = styled.p({
    color: color.steelGray,
    maxWidth: 820,
    marginBottom: 30,
    textAlign: 'center',
    ...font.book(font.size.mediumExtended),
});

const SubTitle = styled.h4(
    {
        textAlign: 'center',
        color: color.fiord,
    },
    font.heavy(font.size.mediumExtended),
);

const TilesWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    maxWidth: 1050,
    [mediaQueries.sm]: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
});

const StyledButton = styled(Button)({
    minWidth: pxToRem(241),
    width: 'fit-content',
});

interface CarePreferenceInfoProps {
    tiles: Record<'title' | 'description' | 'img', string>[];
    title: string;
    description: string | ReactNode;
    subtitle: string;
    onContinue: () => void;
    onCancel: () => void;
    continueLabel: string;
    cancelLabel: string;
    mainImage: string;
}

export const CarePreferenceInfo: React.FC<CarePreferenceInfoProps> = ({
    tiles,
    title,
    description,
    subtitle,
    continueLabel,
    cancelLabel,
    onCancel,
    onContinue,
    mainImage,
}) => {
    return (
        <Wrapper>
            <img src={mainImage} alt="" />
            <Title>{title}</Title>
            <Description>{description}</Description>
            <SubTitle>{subtitle}</SubTitle>
            <TilesWrapper>
                {tiles.map(tileData => (
                    <Tile
                        description={tileData.description}
                        title={tileData.title}
                        key={tileData.description}
                        img={tileData.img}
                    />
                ))}
            </TilesWrapper>
            <div css={{ marginTop: pxToRem(40) }}>
                <ButtonGroup>
                    <StyledButton onClick={onCancel} variant="secondary">
                        {cancelLabel}
                    </StyledButton>
                    <StyledButton onClick={onContinue} variant="primary">
                        {continueLabel}
                    </StyledButton>
                </ButtonGroup>
            </div>
        </Wrapper>
    );
};
